import { parseISO, startOfMonth, addMonths, addDays, startOfDay, differenceInCalendarMonths } from "date-fns";
import {
    getApplicableWeeklyHours,
    getApplicableDayOffConfig,
    isValidDayOffType,
    parseFixedDaysOff,
    getWorkingDaysCount,
    getHolidayCreditedHours,
} from "./calculations";
import { createTimeOffEntries } from "./general";
import { FULL_TIME_WEEKLY_HOURS } from "./constants";
import { StaffWeeklyHours } from "../types";
import {
    AGREEMENT_TYPE_LABELS,
    ContractRun,
    VacationHistorySpan,
    getCurrentRun,
    getVacationRun,
    getVacationHistorySpans,
    hasUnknownAgreementType,
} from "./employmentRun";

// First year computed from live time-off entries. 2025-and-before is not reconstructed from records —
// it comes from the authoritative `unused_vacation_days_at_31_dec_2025` field (see getCarryInHours).
export const VACATION_BASELINE_YEAR = 2026;
export const VACATION_LIVE_START_DATE = `${VACATION_BASELINE_YEAR}-01-01`;

// Portuguese accrual: in the hire year (year one of the current employment run) 2 days per complete
// month capped at 20; from the second year on, 22/12 days per complete month (a full year = exactly 22).
const FIRST_YEAR_DAYS_PER_COMPLETE_MONTH = 2;
const FIRST_YEAR_VACATION_DAYS_CAP = 20;
const LATER_YEARS_DAYS_PER_COMPLETE_MONTH = 22 / 12;
const STANDARD_WORKING_DAYS_PER_WEEK = 5;

type ComputeTimeOffHolidaysSummaryInput = {
    selectedYear: number;
    priorYearsHolidayItems: Array<any> | undefined;
    unusedVacationDaysAt31Dec2025: number | null | undefined;
    thisYearHolidayItems: Array<any> | undefined;
    weeklyHoursSchedule: Array<StaffWeeklyHours>;
    vacationFromFirstRow?: boolean;
    // The day "current relationship" is resolved against — today in the app; explicit in tests.
    referenceDate?: Date;
};

export type TimeOffHolidaysSummary = {
    bookedHours: number;
    entitlementHours: number;
    carryOverHours: number;
    remainingHours: number;
    dailyPattern: number;
    dailyEquivalent: number;
    weeklyHours: number;
    workingDays: number;
    fullTimeEntitlementHours: number;
    agreementTypeMissing: boolean;
    noVacationRights: boolean;
    // Days off booked in the selected year by someone with no vacation rights (service providers) —
    // irrelevant for accounting, needed by HR for resource planning.
    bookedDaysOffCount: number;
    // Manual settlement from accounting (`staff_weekly_hours.holidays_settlement`, in DAYS): the vested
    // unused days the system deliberately does not compute. Added to the pool so HR can schedule them
    // before the leaving day instead of paying them out.
    settlementHours: number;
};

const buildEmptySummary = (
    flags: Partial<Pick<TimeOffHolidaysSummary, "agreementTypeMissing" | "noVacationRights" | "bookedDaysOffCount">>,
) => {
    const summary: TimeOffHolidaysSummary = {
        bookedHours: 0,
        entitlementHours: 0,
        carryOverHours: 0,
        remainingHours: 0,
        dailyPattern: 0,
        dailyEquivalent: 0,
        weeklyHours: 0,
        workingDays: 0,
        fullTimeEntitlementHours: 0,
        agreementTypeMissing: false,
        noVacationRights: false,
        bookedDaysOffCount: 0,
        settlementHours: 0,
        ...flags,
    };
    return summary;
};

const sumSettlementDays = (scheduleRows: Array<StaffWeeklyHours>) => {
    const totalDays = scheduleRows.reduce((sum, scheduleRow) => {
        const days = Number(scheduleRow.holidays_settlement ?? 0);
        return sum + (Number.isFinite(days) ? days : 0);
    }, 0);
    return totalDays;
};

// Days off booked within the CURRENT relationship only — a provider's pre-switch employment vacations
// belong to the settled period, not to HR's planning of the provider.
const countBookedDaysOff = (thisYearHolidayItems: Array<any> | undefined, currentRun: ContractRun | null) => {
    if (!currentRun) return 0;
    const dayEntries = createTimeOffEntries((thisYearHolidayItems ?? []).filter((item) => item?.type === "holiday"));
    const entriesWithinRun = dayEntries.filter((entry) => {
        if (!entry?.start_date) return false;
        const entryDate = parseISO(entry.start_date);
        if (entryDate < currentRun.startDate) return false;
        if (currentRun.endDate && entryDate > currentRun.endDate) return false;
        return true;
    });
    const count = entriesWithinRun.length;
    return count;
};

export const computeTimeOffHolidaysSummary = ({
    selectedYear,
    priorYearsHolidayItems,
    unusedVacationDaysAt31Dec2025,
    thisYearHolidayItems,
    weeklyHoursSchedule,
    vacationFromFirstRow = false,
    referenceDate = new Date(),
}: ComputeTimeOffHolidaysSummaryInput) => {
    if (weeklyHoursSchedule.length === 0 || hasUnknownAgreementType(weeklyHoursSchedule)) {
        const missingSummary = buildEmptySummary({ agreementTypeMissing: true });
        return missingSummary;
    }

    const vacationRun = getVacationRun(weeklyHoursSchedule, vacationFromFirstRow, referenceDate);
    if (!vacationRun) {
        const currentRun = getCurrentRun(weeklyHoursSchedule, referenceDate);
        const noRightsSummary = buildEmptySummary({
            noVacationRights: true,
            bookedDaysOffCount: countBookedDaysOff(thisYearHolidayItems, currentRun),
        });
        return noRightsSummary;
    }

    // A relationship that ended before the selected year was settled at departure — nothing carries.
    const selectedYearStart = new Date(selectedYear, 0, 1);
    if (vacationRun.endDate && vacationRun.endDate < selectedYearStart) {
        const endedSummary = buildEmptySummary({
            noVacationRights: true,
            bookedDaysOffCount: countBookedDaysOff(thisYearHolidayItems, getCurrentRun(weeklyHoursSchedule, referenceDate)),
        });
        return endedSummary;
    }

    const rateProbeDate = getRateProbeDate(vacationRun, selectedYear);
    const weeklyHoursForSelectedYear = getApplicableWeeklyHours(rateProbeDate, vacationRun.rows);
    const dailyPattern = getDailyPattern(weeklyHoursForSelectedYear);
    const workingDaysForSelectedYear = getWorkingDaysForRun(rateProbeDate, vacationRun);
    const dailyEquivalent = workingDaysForSelectedYear > 0 ? weeklyHoursForSelectedYear / workingDaysForSelectedYear : 0;

    const entitledDays = getEntitledDaysForYear(vacationRun, selectedYear);
    const entitlementHours = entitledDays * dailyPattern;
    const carryOverHours = getCarryInHours(vacationRun, selectedYear, unusedVacationDaysAt31Dec2025, priorYearsHolidayItems ?? []);
    const bookedHours = calculateHolidayHoursCredited(thisYearHolidayItems ?? [], vacationRun.rows, vacationRun.endDate, vacationRun.startDate);
    const settlementHours = sumSettlementDays(vacationRun.rows) * dailyPattern;

    const totalEntitlementHours = entitlementHours + carryOverHours + settlementHours;
    const remainingHours = Math.max(0, totalEntitlementHours - bookedHours);

    // Full-time reference (e.g. 176h for a full year) — for the widget tooltip's proportional explanation.
    const fullTimeDailyPattern = getDailyPattern(FULL_TIME_WEEKLY_HOURS);
    const fullTimeEntitlementHours = entitledDays * fullTimeDailyPattern;

    const summary: TimeOffHolidaysSummary = {
        bookedHours,
        entitlementHours,
        carryOverHours,
        remainingHours,
        dailyPattern,
        dailyEquivalent,
        weeklyHours: weeklyHoursForSelectedYear,
        workingDays: workingDaysForSelectedYear,
        fullTimeEntitlementHours,
        agreementTypeMissing: false,
        noVacationRights: false,
        bookedDaysOffCount: 0,
        settlementHours,
    };
    return summary;
};

// The legal/proportional "standard day": the full-time daily standard (40 ÷ 5 = 8h) scaled by the contract's
// weekly hours, capped at full-time. Sizing the entitlement pool and converting hours → legal days both
// use this — NOT the day-off pattern. (Code name `daily_pattern`.)
const getDailyPattern = (weeklyHours: number) => {
    const cappedWeeklyHours = Math.min(weeklyHours, FULL_TIME_WEEKLY_HOURS);
    const dailyPattern = cappedWeeklyHours / STANDARD_WORKING_DAYS_PER_WEEK;
    return dailyPattern;
};

// Rate, pattern and divisor are read at the year's start inside the run (or the run start for its first
// year) — never from rows of another contract period.
const getRateProbeDate = (run: ContractRun, selectedYear: number) => {
    const yearStart = new Date(selectedYear, 0, 1);
    const probeDate = run.startDate > yearStart ? run.startDate : yearStart;
    return probeDate;
};

const getWorkingDaysForRun = (rateProbeDate: Date, run: ContractRun) => {
    let config = getApplicableDayOffConfig(rateProbeDate, run.rows);
    if (!config) {
        const firstValidRow = run.rows.find((scheduleRow) => isValidDayOffType(scheduleRow.day_off_type));
        if (firstValidRow) {
            config = { dayOffType: firstValidRow.day_off_type!, daysOff: parseFixedDaysOff(firstValidRow.fixed_days_off) };
        }
    }
    const workingDays = config ? getWorkingDaysCount(config) : STANDARD_WORKING_DAYS_PER_WEEK;
    return workingDays;
};

// Accrued days for one civil year of the run: 0 outside the run's years; year one → 2 per complete month
// (cap 20); later years → 22/12 per complete month. Complete months are bounded by the run start and the
// run end (proporcional na cessação).
const getEntitledDaysForYear = (run: ContractRun, year: number) => {
    const yearOneYear = run.startDate.getFullYear();
    if (year < yearOneYear) {
        const entitledDays = 0;
        return entitledDays;
    }

    const yearStart = new Date(year, 0, 1);
    const nextYearStart = new Date(year + 1, 0, 1);
    const accrualStart = run.startDate > yearStart ? run.startDate : yearStart;
    const runEndExclusive = run.endDate ? addDays(startOfDay(run.endDate), 1) : null;
    const accrualEndExclusive = runEndExclusive && runEndExclusive < nextYearStart ? runEndExclusive : nextYearStart;

    if (accrualEndExclusive <= accrualStart) {
        const entitledDays = 0;
        return entitledDays;
    }

    const completeMonths = getCompleteMonths(accrualStart, accrualEndExclusive);
    const isYearOne = year === yearOneYear;
    const entitledDays = isYearOne
        ? Math.min(FIRST_YEAR_VACATION_DAYS_CAP, completeMonths * FIRST_YEAR_DAYS_PER_COMPLETE_MONTH)
        : completeMonths * LATER_YEARS_DAYS_PER_COMPLETE_MONTH;
    return entitledDays;
};

// Vacation hours booked, valued exactly as the bag credits them: the ACTUAL entered hours on each
// creditable day (fixed → working days only; flexible → any day). Days outside [startCapDate, endCapDate]
// are excluded — the caps are the employment run's bounds, so vacation dated in a cash/internship/
// contractor period never credits against the employment pool.
export const calculateHolidayHoursCredited = (
    holidayItems: Array<any>,
    weeklyHoursSchedule: Array<StaffWeeklyHours>,
    endCapDate: Date | null,
    startCapDate: Date | null = null,
) => {
    const dayEntries = createTimeOffEntries(holidayItems.filter((item) => item?.type === "holiday"));
    const creditedHours = dayEntries.reduce((sum, entry) => {
        if (!entry?.start_date) return sum;
        const entryDate = parseISO(entry.start_date);
        if (endCapDate && entryDate > endCapDate) return sum;
        if (startCapDate && entryDate < startCapDate) return sum;
        return sum + getHolidayCreditedHours(entry, weeklyHoursSchedule);
    }, 0);
    return creditedHours;
};

const getCompleteMonths = (startDate: Date, endExclusive: Date) => {
    const firstFullMonthStart = startDate.getDate() === 1 ? startDate : startOfMonth(addMonths(startDate, 1));
    const months = Math.max(0, differenceInCalendarMonths(endExclusive, firstFullMonthStart));
    const result = months;
    return result;
};

export type VacationHistoryRow = {
    year: number;
    periodStartDate: Date;
    periodEndDate: Date | null;
    typeLabel: string;
    accrues: boolean;
    entitlementHours: number;
    bookedHours: number;
    differenceHours: number;
    dailyPattern: number;
    isBeforeBaseline: boolean;
    isSettlement?: boolean;
    // Present only on the current vacation run's live years (≥ 2026): the widget's own numbers, computed
    // by computeTimeOffHolidaysSummary so the modal can never disagree with the widget.
    carryInHours?: number;
    poolHours?: number;
};

type ComputeVacationHistoryRowsInput = {
    weeklyHoursSchedule: Array<StaffWeeklyHours>;
    vacationFromFirstRow: boolean;
    holidayItems: Array<any>;
    lastYear: number;
    unusedVacationDaysAt31Dec2025?: number | null;
    referenceDate?: Date;
    agreementTypeLabels?: Record<string, string>;
};

// One row per year × contract period, over EVERY span — non-accruing periods included, so each year's
// holidays are traceable to their contract. Pre-2026 rows are computed from real entries and marked
// `isBeforeBaseline` — indicative only, since `unused_vacation_days_at_31_dec_2025` is the authoritative
// settlement for them.
export const computeVacationHistoryRows = ({
    weeklyHoursSchedule,
    vacationFromFirstRow,
    holidayItems,
    lastYear,
    unusedVacationDaysAt31Dec2025,
    referenceDate = new Date(),
    agreementTypeLabels = AGREEMENT_TYPE_LABELS,
}: ComputeVacationHistoryRowsInput) => {
    if (weeklyHoursSchedule.length === 0 || hasUnknownAgreementType(weeklyHoursSchedule)) {
        return [] as Array<VacationHistoryRow>;
    }

    const historySpans = getVacationHistorySpans(weeklyHoursSchedule, vacationFromFirstRow);
    const currentVacationRun = getVacationRun(weeklyHoursSchedule, vacationFromFirstRow, referenceDate);
    const historyRows: Array<VacationHistoryRow> = [];

    for (const historySpan of historySpans) {
        const startYear = historySpan.startDate.getFullYear();
        const spanEndYear = historySpan.endDate ? historySpan.endDate.getFullYear() : lastYear;
        const endYear = Math.min(spanEndYear, lastYear);

        for (let year = startYear; year <= endYear; year++) {
            const yearStart = new Date(year, 0, 1);
            const yearEnd = new Date(year, 11, 31, 23, 59, 59, 999);
            const periodStartDate = historySpan.startDate > yearStart ? historySpan.startDate : yearStart;
            const isOpenBeyondView = !historySpan.endDate && year === endYear;
            const periodEndDate = isOpenBeyondView
                ? null
                : historySpan.endDate && historySpan.endDate < yearEnd
                    ? historySpan.endDate
                    : yearEnd;

            const dailyPattern = getDailyPattern(getApplicableWeeklyHours(getRateProbeDate(historySpan, year), historySpan.rows));
            const entitlementHours = historySpan.accrues ? getEntitledDaysForYear(historySpan, year) * dailyPattern : 0;
            const yearItems = holidayItems.filter(
                (item) => item?.start_date >= `${year}-01-01` && item?.start_date < `${year + 1}-01-01`
            );
            const bookedHours = historySpan.accrues
                ? calculateHolidayHoursCredited(yearItems, historySpan.rows, historySpan.endDate, historySpan.startDate)
                : 0;

            const historyRow: VacationHistoryRow = {
                year,
                periodStartDate,
                periodEndDate,
                typeLabel: getYearTypeLabel(historySpan, year, agreementTypeLabels),
                accrues: historySpan.accrues,
                entitlementHours,
                bookedHours,
                differenceHours: entitlementHours - bookedHours,
                dailyPattern,
                isBeforeBaseline: year < VACATION_BASELINE_YEAR,
            };

            // Live years of the CURRENT vacation run take the widget's numbers verbatim — one function,
            // one truth: carry-in and settlement extend the pool exactly as the Férias widget shows them.
            const isCurrentVacationRunSpan =
                historySpan.accrues &&
                currentVacationRun !== null &&
                historySpan.startDate.getTime() === currentVacationRun.startDate.getTime();
            if (isCurrentVacationRunSpan && year >= VACATION_BASELINE_YEAR) {
                const yearSummary = computeTimeOffHolidaysSummary({
                    selectedYear: year,
                    priorYearsHolidayItems: holidayItems.filter((item) => item?.start_date < `${year}-01-01`),
                    unusedVacationDaysAt31Dec2025,
                    thisYearHolidayItems: yearItems,
                    weeklyHoursSchedule,
                    vacationFromFirstRow,
                    referenceDate,
                });
                historyRow.entitlementHours = yearSummary.entitlementHours;
                historyRow.bookedHours = yearSummary.bookedHours;
                historyRow.differenceHours = yearSummary.entitlementHours - yearSummary.bookedHours;
                historyRow.carryInHours = yearSummary.carryOverHours;
                historyRow.poolHours =
                    yearSummary.entitlementHours + yearSummary.carryOverHours + yearSummary.settlementHours;
            }

            historyRows.push(historyRow);
        }

        // The manual settlement is a distinct fact with its own provenance — it gets its own row in the
        // span's closing year rather than polluting the rule-derived accrual numbers.
        const settlementDays = sumSettlementDays(historySpan.rows);
        if (historySpan.accrues && settlementDays > 0) {
            const settlementYear = Math.min(historySpan.endDate ? historySpan.endDate.getFullYear() : lastYear, lastYear);
            const settlementDailyPattern = getDailyPattern(
                getApplicableWeeklyHours(getRateProbeDate(historySpan, settlementYear), historySpan.rows),
            );
            const settlementDate = historySpan.endDate ?? new Date(settlementYear, 11, 31);
            historyRows.push({
                year: settlementYear,
                periodStartDate: settlementDate,
                periodEndDate: settlementDate,
                typeLabel: "Acerto (contabilidade)",
                accrues: false,
                entitlementHours: settlementDays * settlementDailyPattern,
                bookedHours: 0,
                differenceHours: settlementDays * settlementDailyPattern,
                dailyPattern: settlementDailyPattern,
                isBeforeBaseline: settlementYear < VACATION_BASELINE_YEAR,
                isSettlement: true,
            });
        }
    }

    const sortedRows = historyRows.sort((rowA, rowB) => {
        if (rowA.year !== rowB.year) return rowA.year - rowB.year;
        return rowA.periodStartDate.getTime() - rowB.periodStartDate.getTime();
    });
    return sortedRows;
};

// The contract type(s) active during one year of a span — a flag-on span crossing cash → IEFP shows both.
const getYearTypeLabel = (historySpan: VacationHistorySpan, year: number, agreementTypeLabels: Record<string, string>) => {
    const yearStartString = `${year}-01-01`;
    const yearEndString = `${year}-12-31`;
    const labels = historySpan.rows
        .filter((scheduleRow) => {
            const startsBeforeYearEnds = scheduleRow.date_start <= yearEndString;
            const endsAfterYearStarts = !scheduleRow.date_end || scheduleRow.date_end >= yearStartString;
            return startsBeforeYearEnds && endsAfterYearStarts;
        })
        .map((scheduleRow) => agreementTypeLabels[scheduleRow.agreement_type ?? ""] ?? "—");
    const distinctLabels = Array.from(new Set(labels));
    const label = distinctLabels.length > 0 ? distinctLabels.join(" + ") : "—";
    return label;
};

// Carry-in from before the selected year, scoped to the current employment run. The 31 Dec 2025 baseline
// (`unused_vacation_days_at_31_dec_2025`) is authoritative for pre-2026 history, but belongs to the run
// only when the run had already started by then — a run that begins after the baseline date starts clean
// (decision: a switch settles everything). Completed live years inside the run then accumulate
// entitlement earned minus booked.
const getCarryInHours = (
    run: ContractRun,
    selectedYear: number,
    unusedVacationDaysAt31Dec2025: number | null | undefined,
    priorYearsHolidayItems: Array<any>,
) => {
    const yearOneYear = run.startDate.getFullYear();

    const baselineApplies = run.startDate < parseISO(VACATION_LIVE_START_DATE);
    const baselineRate = getApplicableWeeklyHours(getRateProbeDate(run, VACATION_BASELINE_YEAR), run.rows);
    const baselineHours = baselineApplies ? (unusedVacationDaysAt31Dec2025 || 0) * getDailyPattern(baselineRate) : 0;

    let accumulated = 0;
    for (let year = Math.max(VACATION_BASELINE_YEAR, yearOneYear); year < selectedYear; year++) {
        const dailyPatternForYear = getDailyPattern(getApplicableWeeklyHours(getRateProbeDate(run, year), run.rows));
        const entitlement = getEntitledDaysForYear(run, year) * dailyPatternForYear;
        const yearItems = priorYearsHolidayItems.filter(
            (item) => item?.start_date >= `${year}-01-01` && item?.start_date < `${year + 1}-01-01`
        );
        const booked = calculateHolidayHoursCredited(yearItems, run.rows, run.endDate, run.startDate);
        accumulated += entitlement - booked;
    }

    const carryIn = Math.max(0, baselineHours + accumulated);
    return carryIn;
};
